// @ts-nocheck
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface RegistrationTier {
  name: string;
  price: number;
  description?: string;
  form_fields?: string[];
}

interface DivisionPricing {
  name: string;
  price: number;
  registration_form_id?: string;
  form_fields?: string[];
  accept_special_requests?: boolean;
  special_request_instructions?: string;
  special_request_fee?: number;
}

interface RosterConfig {
  min_players?: number;
  max_players?: number;
  deadline?: string;
}

interface RegistrationPricingTabProps {
  eventId?: string;
  pricingType: "individual" | "team";
  setPricingType: (type: "individual" | "team") => void;
  registrationTiers: RegistrationTier[];
  divisionPricing: DivisionPricing[];
  rosterConfig: RosterConfig;
  onUpdate: (updates: {
    registration_tiers?: RegistrationTier[];
    division_pricing?: DivisionPricing[];
    roster_config?: RosterConfig;
  }) => void;
}

export default function RegistrationPricingTab({
  eventId,
  pricingType,
  setPricingType,
  registrationTiers,
  divisionPricing,
  rosterConfig,
  onUpdate,
}: RegistrationPricingTabProps) {
  const [enableRosterManagement, setEnableRosterManagement] = useState(
    !!(rosterConfig.min_players || rosterConfig.max_players || rosterConfig.deadline)
  );
  const [availableForms, setAvailableForms] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (eventId && pricingType === 'team') {
      loadTeamForms();
    }
  }, [eventId, pricingType]);

  async function loadTeamForms() {
    // Query all forms without event filtering since forms are defined by event_type/category, not specific events
    const { data, error } = await supabase
      .from('event_registration_forms')
      .select('id, name');
    
    if (error) {
      console.error('Error loading forms:', error);
    }
    
    if (data) {
      setAvailableForms(data);
    }
  }

  const addTier = () => {
    onUpdate({
      registration_tiers: [
        ...registrationTiers,
        { name: "", price: 0, description: "" },
      ],
    });
  };

  const updateTier = (index: number, field: keyof RegistrationTier, value: any) => {
    const updated = [...registrationTiers];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate({ registration_tiers: updated });
  };

  const removeTier = (index: number) => {
    onUpdate({
      registration_tiers: registrationTiers.filter((_, i) => i !== index),
    });
  };

  const addDivision = () => {
    onUpdate({
      division_pricing: [
        ...divisionPricing,
        { name: "", price: 0 },
      ],
    });
  };

  const updateDivision = (index: number, field: keyof DivisionPricing, value: any) => {
    const updated = [...divisionPricing];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate({ division_pricing: updated });
  };

  const removeDivision = (index: number) => {
    onUpdate({
      division_pricing: divisionPricing.filter((_, i) => i !== index),
    });
  };

  const updateRosterConfig = (field: keyof RosterConfig, value: any) => {
    onUpdate({
      roster_config: { ...rosterConfig, [field]: value },
    });
  };

  return (
    <div className="space-y-6">
      {/* Registration Type */}
      <Card>
        <CardHeader>
          <CardTitle>Registration Type</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={pricingType}
            onValueChange={(value) => setPricingType(value as "individual" | "team")}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="individual" id="individual" />
              <Label htmlFor="individual">Individual Registration</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="team" id="team" />
              <Label htmlFor="team">Team Registration</Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Individual Pricing Tiers */}
      {pricingType === "individual" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Pricing Tiers</CardTitle>
              <Button onClick={addTier} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Tier
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {registrationTiers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No pricing tiers configured. Click "Add Tier" to get started.
              </p>
            ) : (
              registrationTiers.map((tier, index) => (
                <Card key={index} className="border-border/50">
                  <CardContent className="pt-4 space-y-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 space-y-4">
                        <div>
                          <Label>Tier Name</Label>
                          <Input
                            placeholder="e.g., Early Bird, Regular, VIP"
                            value={tier.name}
                            onChange={(e) => updateTier(index, "name", e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Price (cents)</Label>
                          <div className="space-y-1">
                            <Input
                              type="number"
                              placeholder="2000"
                              value={tier.price || ""}
                              onChange={(e) => updateTier(index, "price", parseInt(e.target.value) || 0)}
                            />
                            <p className="text-xs text-muted-foreground">
                              = ${((tier.price || 0) / 100).toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <div>
                          <Label>Description (optional)</Label>
                          <Textarea
                            placeholder="Tier description..."
                            value={tier.description || ""}
                            onChange={(e) => updateTier(index, "description", e.target.value)}
                            rows={2}
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTier(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Team Division Pricing */}
      {pricingType === "team" && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Division Pricing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {divisionPricing.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No divisions configured. Click "Add Division" to get started.
                </p>
              ) : (
                divisionPricing.map((division, index) => (
                  <Card key={index} className="border-border/50">
                    <CardContent className="pt-4 space-y-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 space-y-4">
                          <div>
                            <Label>Division Name</Label>
                            <Input
                              placeholder="e.g., 11U, 12U, Varsity"
                              value={division.name}
                              onChange={(e) => updateDivision(index, "name", e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Price (cents)</Label>
                            <div className="space-y-1">
                              <Input
                                type="number"
                                placeholder="15000"
                                value={division.price || ""}
                                onChange={(e) => updateDivision(index, "price", parseInt(e.target.value) || 0)}
                              />
                              <p className="text-xs text-muted-foreground">
                                = ${((division.price || 0) / 100).toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <div>
                            <Label>Registration Form</Label>
                            <Select
                              value={division.registration_form_id || ""}
                              onValueChange={(value) => updateDivision(index, "registration_form_id", value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select form for this division" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableForms.map((form) => (
                                  <SelectItem key={form.id} value={form.id}>
                                    {form.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">
                              Assign a custom registration form for this division
                            </p>
                          </div>
                          <div className="space-y-3 pt-4 border-t">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`division-special-requests-${index}`}
                                checked={division.accept_special_requests || false}
                                onCheckedChange={(checked) => 
                                  updateDivision(index, "accept_special_requests", checked as boolean)
                                }
                              />
                              <Label htmlFor={`division-special-requests-${index}`} className="text-sm font-medium">
                                Accept special requests for this division
                              </Label>
                            </div>
                            {division.accept_special_requests && (
                              <div className="space-y-3 pl-6 border-l-2">
                                <div>
                                  <Label className="text-xs">Instructions</Label>
                                  <Textarea
                                    placeholder="e.g., List any special accommodations needed..."
                                    value={division.special_request_instructions || ""}
                                    onChange={(e) => 
                                      updateDivision(index, "special_request_instructions", e.target.value)
                                    }
                                    rows={2}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Special Request Fee ($)</Label>
                                  <div className="space-y-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      placeholder="0"
                                      value={division.special_request_fee ?? 0}
                                      onChange={(e) =>
                                        updateDivision(index, "special_request_fee", parseFloat(e.target.value) || 0)
                                      }
                                      className="text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      Optional fee charged when a team submits a special request for this age group. Leave $0 if special requests are free.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDivision(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
              <div className="flex justify-end pt-2">
                <Button onClick={addDivision} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Division
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Roster Management */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Roster Management</CardTitle>
                <Switch
                  checked={enableRosterManagement}
                  onCheckedChange={setEnableRosterManagement}
                />
              </div>
            </CardHeader>
            {enableRosterManagement && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Minimum Players</Label>
                    <Input
                      type="number"
                      placeholder="8"
                      value={rosterConfig.min_players || ""}
                      onChange={(e) => updateRosterConfig("min_players", parseInt(e.target.value) || undefined)}
                    />
                  </div>
                  <div>
                    <Label>Maximum Players</Label>
                    <Input
                      type="number"
                      placeholder="15"
                      value={rosterConfig.max_players || ""}
                      onChange={(e) => updateRosterConfig("max_players", parseInt(e.target.value) || undefined)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Roster Deadline</Label>
                  <Input
                    type="date"
                    value={rosterConfig.deadline || ""}
                    onChange={(e) => updateRosterConfig("deadline", e.target.value)}
                  />
                </div>
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
}